<script setup lang="ts">
import { computed } from 'vue';
import { DEFAULT_LABELS, priceTag, type CalendarLabels, type DayAvailability, type VenueFilter } from './types';

const {
  day,
  state,
  availability = undefined,
  showVenueBar = false,
  filter = 'both',
  selected = false,
  tabbable = false,
  sample = false,
  labels = DEFAULT_LABELS,
} = defineProps<{
  day: string | null;
  state: 'available' | 'unavailable' | 'blank';
  availability?: DayAvailability;
  showVenueBar?: boolean;
  /** Active venue filter: 'both' draws the split bar, a venue draws one full-width bar in that venue's color. */
  filter?: VenueFilter;
  selected?: boolean;
  tabbable?: boolean;
  sample?: boolean;
  labels?: CalendarLabels;
}>();

const emit = defineEmits<{ (e: 'select', day: string): void }>();

const dayNum = computed(() => (day ? Number(day.slice(8, 10)) : ''));

// Local date from parts — never new Date('YYYY-MM-DD') (UTC off-by-one).
const longDate = computed(() => {
  if (!day) return '';
  const d = new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  return new Intl.DateTimeFormat('fr', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
});

const tag = computed(() => (availability ? priceTag(availability.price) : null));

// Both cheaper-than-base categories read the same to screen readers; the
// selection summary spells out the exact nature and amount (spec round 2).
const PRICE_WORDS: Record<string, string> = { special: 'tarif réduit', discount: 'tarif réduit' };

// The only venue/price channel for screen readers — populated regardless of
// the filter view (spec §5).
const ariaLabel = computed(() => {
  if (!day) return '';
  const parts = [longDate.value];
  if (state === 'unavailable' || !availability) {
    parts.push('indisponible');
  } else {
    parts.push(`La Roue ${availability.venues.includes('lasalle') ? labels.available : labels.full}`);
    parts.push(`La Serre ${availability.venues.includes('serre') ? labels.available : labels.full}`);
    if (PRICE_WORDS[availability.price]) parts.push(PRICE_WORDS[availability.price]);
  }
  if (selected) parts.push('sélectionné');
  return parts.join(', ');
});

const segOn = (venue: string) => availability?.venues.includes(venue) ?? false;

function onClick() {
  if (state === 'unavailable') return;
  emit('select', day!);
}
</script>

<template>
  <span v-if="day === null" class="day-blank" />
  <span v-else-if="sample" aria-hidden="true" class="day-cell" :data-selected="selected || undefined">
    <span v-if="tag" class="day-tag">{{ tag }}</span>
    <span class="day-num" :class="{ 'day-num--off': state === 'unavailable', 'day-num--selected': selected }">{{ dayNum }}</span>
    <span v-if="showVenueBar && state === 'available'" class="day-bar">
      <template v-if="filter === 'both'">
        <span class="day-seg day-seg--lasalle" :class="segOn('lasalle') ? 'day-seg--on' : 'day-seg--off'" />
        <span class="day-seg day-seg--serre" :class="segOn('serre') ? 'day-seg--on' : 'day-seg--off'" />
      </template>
      <span v-else class="day-seg day-seg--on" :class="`day-seg--${filter}`" />
    </span>
  </span>
  <button
    v-else
    type="button"
    class="day-cell"
    :class="{ 'day-cell--unavailable': state === 'unavailable' }"
    :aria-disabled="state === 'unavailable' || undefined"
    :tabindex="tabbable ? 0 : -1"
    :data-date="day"
    :data-selected="selected || undefined"
    :aria-label="ariaLabel"
    :aria-pressed="selected"
    @click="onClick"
  >
    <span v-if="tag" class="day-tag">{{ tag }}</span>
    <span class="day-num" :class="{ 'day-num--off': state === 'unavailable', 'day-num--selected': selected }">{{ dayNum }}</span>
    <span v-if="showVenueBar && state === 'available'" class="day-bar">
      <template v-if="filter === 'both'">
        <span class="day-seg day-seg--lasalle" :class="segOn('lasalle') ? 'day-seg--on' : 'day-seg--off'" />
        <span class="day-seg day-seg--serre" :class="segOn('serre') ? 'day-seg--on' : 'day-seg--off'" />
      </template>
      <span v-else class="day-seg day-seg--on" :class="`day-seg--${filter}`" />
    </span>
  </button>
</template>

<style scoped>
.day-cell {
  --venue-lasalle: #8b5a3c;
  --venue-serre: #6b8f5e;
  --venue-ghost: #dcd8d2;
  --day-selected: #ffbd59;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 44px;
  min-height: 44px;
  padding: 0 0 8px;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
}
.day-cell--unavailable { cursor: not-allowed; }
.day-blank { display: block; min-height: 44px; }

.day-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #1d1d1d;
}
.day-num--off {
  color: #9a9a9a;
  text-decoration: line-through;
}
.day-num--selected {
  background-color: color-mix(in srgb, var(--day-selected) 50%, transparent);
  outline: 2px solid var(--day-selected);
  color: black;
}

.day-tag {
  position: absolute;
  top: 2px;
  right: 3px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: #b25900;
  pointer-events: none;
}

/* Availability underline. Both view: split bar, left = La Roue, right = La Serre, both segments always drawn (position is the identity channel). Filtered view: one full-width segment in the active venue's color, echoing the tab. */
.day-bar {
  position: absolute;
  bottom: 3px;
  left: 4px;
  right: 4px;
  display: flex;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  pointer-events: none;
}
.day-seg { flex: 1; }
.day-seg--lasalle.day-seg--on { background-color: var(--venue-lasalle); }
.day-seg--serre.day-seg--on { background-color: var(--venue-serre); }
.day-seg--off { background-color: var(--venue-ghost); }

.day-cell:not(.day-cell--unavailable):hover .day-num:not(.day-num--selected) { background-color: #eeeae4; }
.day-cell:focus-visible { outline: 2px solid #1d1d1d; outline-offset: -2px; }

/* Windows High Contrast: background-color is stripped — redraw with system
   colors so the bar survives (spec §2). */
@media (forced-colors: active) {
  .day-seg--on { background-color: CanvasText !important; }
  .day-seg--off { background-color: transparent !important; border: 1px solid GrayText; }
  .day-num--selected { outline: 2px solid Highlight; }
}
</style>
